import React, { useEffect, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import { CircularProgress } from "@mui/material";
import { VideoPreview } from "../../../shared/interfaces/VideoPreview";
import { EvidenceCard } from "../EvidenceCard";
import SkeletonEvidenceCard from "../../../components/skeletons/SkeletonEvidenceCard";
import { EmptyVideosState } from "./EmptyVideosState";

interface VideoGridProps {
  isLoading: boolean;
  paginatedVideos: VideoPreview[];
  selectedVideos: string[];
  onVideoSelect: (videoId: string, isSelected: boolean) => void;
  // Infinite scroll props
  onLoadMore?: () => void;
  hasMore?: boolean;
  // Empty state props
  hasActiveFilters: boolean;
  onResetFilters: () => void;
}

export const VideoGrid: React.FC<VideoGridProps> = ({
  isLoading,
  paginatedVideos,
  selectedVideos,
  onVideoSelect,
  onLoadMore,
  hasMore,
  hasActiveFilters,
  onResetFilters,
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Infinite Scroll Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading && hasMore && onLoadMore) {
          onLoadMore();
        }
      },
      { rootMargin: "200px" },
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [isLoading, hasMore, onLoadMore]);

  const renderSkeletonCards = () => {
    return Array(12)
      .fill(0)
      .map((_, index) => <SkeletonEvidenceCard key={`skeleton-${index}`} />);
  };

  const [hasLoaded, setHasLoaded] = useState(!isLoading);

  useEffect(() => {
    if (!isLoading && !hasLoaded) {
      setHasLoaded(true);
    }
  }, [isLoading, hasLoaded]);

  const isInitialLoading = isLoading && !hasLoaded;
  const isEmpty = !isLoading && paginatedVideos.length === 0;

  if (isEmpty) {
    return (
      <Grid size={{ xs: 12 }}>
        <EmptyVideosState
          hasActiveFilters={hasActiveFilters}
          onResetFilters={onResetFilters}
        />
      </Grid>
    );
  }

  return (
    <Grid size={{ xs: 12 }}>
      <Grid
        container
        spacing={2}
        justifyContent="center"
        alignContent="flex-start"
        sx={{
          minHeight: { xs: "auto", sm: "550px" },
          maxWidth: "100%",
        }}
      >
        {isInitialLoading
          ? renderSkeletonCards()
          : paginatedVideos.map((video) => (
              <EvidenceCard
                video={video}
                key={`${video.id}-${video.status}`}
                isSelected={selectedVideos.includes(video.id)}
                onSelect={onVideoSelect}
                numberSelected={selectedVideos.length}
              />
            ))}

        {/* Sentinel for infinite scroll - wrapped in Grid to maintain layout */}
        <Grid size={{ xs: 12 }}>
          <div
            ref={sentinelRef}
            style={{ width: "100%", height: "20px", marginTop: "10px" }}
          />
        </Grid>

        {/* Loading indicator for appending */}
        {!isInitialLoading && isLoading && (
          <Grid
            size={{ xs: 12 }}
            sx={{ display: "flex", justifyContent: "center", p: 2 }}
          >
            <CircularProgress />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
